import {
  AppConfigurationClient,
  featureFlagPrefix,
  parseFeatureFlag,
} from "@azure/app-configuration";
import { useCallback, useEffect } from "react";
import { Dictionary } from "@/contexts/ConfigContext";
import { useConfigContext } from "@/hooks/useConfigContext";

const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT ?? "";

interface Config {
  ApiUrl?: string;
  [key: string]: string | undefined;
}

export default function useConfig(): {
  config: Config | null;
  getConfig: () => Config;
  getFeatureFlags: () => Dictionary<boolean>;
} {
  const configCtx = useConfigContext();
  const {
    config: rawConfig,
    setConfig,
    featureFlags,
    setFeatureFlags,
  } = configCtx;
  const config: Config | null = rawConfig ? (rawConfig as Config) : null;

  useEffect(() => {
    const loadConfig = async () => {
      // READONLY CONNECTIONSTRING ONLY!
      const client = new AppConfigurationClient(
        "Endpoint=https://bp-public-config.azconfig.io;Id=YGfk;Secret=7+e+EOAwn5qzNxKtQMk8h/GltRwikTkPYnrNgUsqnfg=",
      );

      const configs = client.listConfigurationSettings({
        labelFilter: ENVIRONMENT,
      });

      const configSettings: Dictionary<string> = new Dictionary<string>();
      const featureFlagSettings: Dictionary<boolean> =
        new Dictionary<boolean>();

      for await (const setting of configs) {
        const val =
          setting.value !== undefined
            ? setting
            : await client.getConfigurationSetting({
                key: setting.key,
                label: ENVIRONMENT,
              });

        if (val.key.startsWith(featureFlagPrefix) && val.value) {
          const flag = parseFeatureFlag(val);
          featureFlagSettings[flag.value.id ?? ""] = flag.value.enabled;
        } else if (val.value) {
          configSettings[setting.key] = val.value;
        }
      }
      setConfig(configSettings);
      setFeatureFlags(featureFlagSettings);
    };

    if (!config) {
      void loadConfig();
    }
  }, [config, setConfig, setFeatureFlags]);

  const getConfig = useCallback((): Config => {
    return config ?? ({} as Config);
  }, [config]);

  const getFeatureFlags = useCallback(() => {
    return featureFlags ?? new Dictionary<boolean>();
  }, [featureFlags]);

  return { config, getConfig, getFeatureFlags };
}
